/**
 * Shared amount-input helpers for expense/payment forms. Handles both
 * European ("1.234,56") and US ("1,234.56") decimal notations.
 */

/** Parse a free-typed amount string into a rounded number (2dp). Invalid → 0. */
export function parseAmount(raw: string): number {
  const s = raw.trim().replace(/\s/g, "");
  if (!s) return 0;
  // If both . and , present, treat "," as decimal only if it's last
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  let cleaned: string;
  if (lastComma > lastDot) {
    // European format: 1.234,56
    cleaned = s.replace(/\./g, "").replace(",", ".");
  } else {
    // US format: 1,234.56
    cleaned = s.replace(/,/g, "");
  }
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

/** "€", "$", "£", "¥" for known codes; otherwise the code + a trailing space. */
export function currencySymbol(code: string): string {
  const map: Record<string, string> = { EUR: "€", USD: "$", GBP: "£", JPY: "¥" };
  return map[code] ?? code + " ";
}

/** yyyy-MM-dd in the viewer's LOCAL timezone. */
export function todayLocalYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Parse yyyy-MM-dd as local noon (12:00). Using noon rather than
 * midnight sidesteps DST + day-bucketing edge cases where a
 * midnight timestamp could bucket into the previous day.
 */
export function parseYmdLocalNoon(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
}
